import type { TaskType, TeamType, UserType } from '@api';
import { TaskFormSchemaType } from '@common';
import { Group, Modal, Stack } from '@mantine/core';
import {
  TaskDetailsDisplay,
  TaskDetailsEditForm,
  TaskDetailsModalTitle,
  TaskPriorityBadge,
  TaskStatusBadge,
} from './components';

export type TaskDetailsModalModeType = 'display' | 'edit';

type TaskDetailsModalBasePropsType = {
  opened: boolean;
  onClose: () => void;
  task: TaskType | null;
};

type TaskDetailsModalReadOnlyPropsType = TaskDetailsModalBasePropsType & {
  canEdit?: false;
  mode?: 'display';
};

type TaskDetailsModalEditablePropsType = TaskDetailsModalBasePropsType & {
  canEdit: true;
  mode: TaskDetailsModalModeType;
  onModeChange: (mode: TaskDetailsModalModeType) => void;
  onSubmit: (data: TaskFormSchemaType) => Promise<void>;
  users: UserType[];
  teams: TeamType[];
};

export type TaskDetailsModalPropsType =
  | TaskDetailsModalReadOnlyPropsType
  | TaskDetailsModalEditablePropsType;

export const TaskDetailsModal: React.FC<TaskDetailsModalPropsType> = (
  props
) => {
  const { opened, onClose, task } = props;
  const canEdit = props.canEdit === true;
  const mode = canEdit ? props.mode : 'display';

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="xl"
      title={
        <TaskDetailsModalTitle
          mode={mode}
          title={task?.title ?? ''}
          canEdit={canEdit && task !== null && task.status === 'OPEN'}
          onEdit={() => {
            if (canEdit) {
              props.onModeChange('edit');
            }
          }}
        />
      }
      closeOnClickOutside={false}
      styles={{ title: { flex: 1 }, header: { alignItems: 'center' } }}
    >
      {task ? (
        <Stack gap="lg">
          <Group wrap="nowrap">
            <TaskStatusBadge status={task.status} />
            {mode === 'display' ? (
              <TaskPriorityBadge priority={task.priority} />
            ) : null}
          </Group>
          {mode === 'edit' && canEdit ? (
            <TaskDetailsEditForm
              key={task.id}
              task={task}
              onSubmit={props.onSubmit}
              onCancel={() => props.onModeChange('display')}
              users={props.users}
              teams={props.teams}
            />
          ) : (
            <TaskDetailsDisplay key={task.id} task={task} />
          )}
        </Stack>
      ) : null}
    </Modal>
  );
};
